// src/context/AuthContext.tsx
import React, { createContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAuth,
  signInWithEmailAndPassword, 
  signOut,
  onAuthStateChanged,
  updateProfile,
  type User as FirebaseUser
} from 'firebase/auth';

interface User {
  id: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  role: 'admin' | 'manager';
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;  // Added this line
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUserProfile: (data: { displayName?: string; photoURL?: string }) => Promise<void>;
  error: string | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);  // Added this line
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const auth = getAuth();

  // Convert Firebase user to our User type
  const formatUser = (firebaseUser: FirebaseUser): User => ({
    id: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    role: 'admin' // You might want to fetch this from your database
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setIsLoading(true);  // Added this line
      if (firebaseUser) {
        setUser(formatUser(firebaseUser));
      } else {
        setUser(null);
      }
      setIsLoading(false);  // Added this line
    });

    return () => unsubscribe();
  }, [auth]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      setError(null);
      setIsLoading(true);  // Added this line
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      setUser(formatUser(userCredential.user));
    } catch (error) {
      console.error('Login error:', error);
      setError('Invalid credentials');
      throw error;
    } finally {
      setIsLoading(false);  // Added this line
    }
  }, [auth]);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);  // Added this line
      await signOut(auth);
      setUser(null);
      navigate('/auth/login');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setIsLoading(false);  // Added this line
    }
  }, [auth, navigate]);

  const updateUserProfile = useCallback(async (data: { displayName?: string; photoURL?: string }) => {
    try {
      setIsLoading(true);  // Added this line
      if (!auth.currentUser) throw new Error('No authenticated user');
      await updateProfile(auth.currentUser, data);
      if (user) {
        setUser({
          ...user,
          ...data
        });
      }
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    } finally {
      setIsLoading(false);  // Added this line
    }
  }, [auth, user]);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,  // Added this line
      login,
      logout,
      updateUserProfile,
      error
    }}>
      {children}
    </AuthContext.Provider>
  );
};